export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * `default` is the auth-funnel sizing (16px control text, roomy padding) the signup and
 * waitlist forms are built around. `compact` matches the in-app 14px form density.
 */
export type SelectInputSize = "default" | "compact";

const SIZE_CLASSES: Record<SelectInputSize, { wrapper: string; select: string }> = {
  default: { wrapper: "mb-5", select: "px-4 py-3" },
  compact: { wrapper: "mb-3", select: "px-3 py-2 text-sm" },
};

interface SelectInputViewProps {
  id?: string;
  name?: string;
  value: string;
  options: SelectOption[];
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  size?: SelectInputSize;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void;
  className?: string;
}

export const SelectInputView = ({
  id,
  name,
  value,
  options,
  placeholder,
  label,
  error,
  disabled = false,
  required = false,
  size = "default",
  onChange,
  onBlur,
  className = "",
}: SelectInputViewProps) => {
  const sizeClasses = SIZE_CLASSES[size];
  const selectClasses = `block w-full ${sizeClasses.select} text-foreground border rounded-lg bg-surface-subtle focus-visible:shadow-focus transition-all duration-200 outline-none ${
    error ? "border-destructive" : "border-input"
  } ${disabled ? "bg-surface-sunken cursor-not-allowed opacity-60" : ""} ${className}`;

  return (
    <div className={sizeClasses.wrapper}>
      {label && (
        <label
          htmlFor={id || name}
          className="mb-2 block text-sm font-semibold text-muted-foreground"
        >
          {label}
          {required && <span className="ml-1 text-destructive">*</span>}
        </label>
      )}
      <select
        id={id || name}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        className={selectClasses}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      {error && <p className="mt-1.5 text-xs font-medium text-destructive-text">{error}</p>}
    </div>
  );
};
